// src/services/nftFetcher/infinaeonNFTFetcher.js
import secureFetch from '../../utils/secureFetch';
import { standardizeNFTFormat } from './nftFormatUtils';
import { fetchContractMetadata } from './contractMetadataFetcher';

/**
 * Fetch NFTs for an Infinaeon wallet using the Infinaeon explorer API.
 * Note: Infinaeon API returns NFT collections with counts rather than individual instances
 *
 * @param {string} walletAddress - The wallet address to query
 * @returns {Promise<Array>} - A promise that resolves to an array of NFT objects
 */
export const fetchNFTs = async (walletAddress) => {
  try {
    const response = await secureFetch(`https://explorer.infinaeon.com/api/v2/addresses/${walletAddress}/tokens?type=ERC-721`);
    
    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Check if we have the expected structure with items array
    if (!data || !Array.isArray(data.items)) {
      console.warn("Unexpected Infinaeon NFT API response format:", data);
      return [];
    }
    
    // Transform the data to match our standardized format
    const nfts = [];
    
    // Process each collection from the API
    for (const item of data.items) {
      try {
        // Infinaeon API returns collections with counts, not individual NFTs
        const count = parseInt(item.value) || 1;
        
        // For each NFT in the collection, try to fetch real token IDs
        // We'll attempt to get metadata for the first few tokens to see what exists
        const maxTokensToCheck = Math.min(count, 10); // Limit checking to avoid too many calls
        
        for (let i = 1; i <= maxTokensToCheck; i++) {
          try {
            // Try to fetch metadata for this token ID
            const metadata = await fetchContractMetadata(item.token?.address, i.toString(), 'infinaeon');
            
            const nftData = {
              // Infinaeon format for standardization
              token: {
                address: item.token?.address,
                name: item.token?.name,
                symbol: item.token?.symbol,
                type: item.token?.type || 'ERC-721',
                metadata: metadata // Add the fetched metadata
              },
              token_id: i.toString(),
              value: "1" // Each individual NFT has value of 1
            };
            
            const standardizedNFT = standardizeNFTFormat(nftData, 'infinaeon');
            if (standardizedNFT) {
              // Add collection info to help identify that these are from the same collection
              standardizedNFT.nft.collectionCount = count;
              standardizedNFT.nft.hasRealTokenId = !!metadata; // Mark if we got real metadata
              nfts.push(standardizedNFT);
            }
          } catch (tokenError) {
            console.warn(`Could not fetch metadata for ${item.token?.address}:${i}`, tokenError);
            
            // If metadata fetching fails, create a basic NFT object
            const nftData = {
              token: {
                address: item.token?.address,
                name: item.token?.name,
                symbol: item.token?.symbol,
                type: item.token?.type || 'ERC-721'
              },
              token_id: i.toString(),
              value: "1"
            };
            
            const standardizedNFT = standardizeNFTFormat(nftData, 'infinaeon');
            if (standardizedNFT) {
              standardizedNFT.nft.collectionCount = count;
              standardizedNFT.nft.virtualTokenId = true; // Mark as virtual since we don't have real metadata
              nfts.push(standardizedNFT);
            }
          }
        }
        
        // If we have more than what we checked, create virtual NFTs for the rest
        if (count > maxTokensToCheck) {
          for (let i = maxTokensToCheck + 1; i <= count; i++) {
            const nftData = {
              token: {
                address: item.token?.address,
                name: item.token?.name,
                symbol: item.token?.symbol,
                type: item.token?.type || 'ERC-721'
              },
              token_id: i.toString(),
              value: "1"
            };
            
            const standardizedNFT = standardizeNFTFormat(nftData, 'infinaeon');
            if (standardizedNFT) {
              standardizedNFT.nft.collectionCount = count;
              standardizedNFT.nft.virtualTokenId = true; // Mark as virtual
              nfts.push(standardizedNFT);
            }
          }
        }
      } catch (error) {
        console.error("Error processing Infinaeon NFT collection:", error, item);
      }
    }
    
    console.log(`Fetched ${nfts.length} NFTs from ${data.items.length} collections for Infinaeon address ${walletAddress}`);
    return nfts;
    
  } catch (error) {
    console.error("Error fetching Infinaeon NFTs:", error);
    return [];
  }
};